import { useStyletron } from '@thanx/uikit/theme'
import React, { useCallback, useEffect, useRef } from 'react'

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

export interface InlineSelectProps
  extends React.HTMLAttributes<HTMLSpanElement> {
  /** Whether the dropdown menu is open */
  isOpen?: boolean
  /** Whether the select is disabled */
  disabled?: boolean
  /** Called when user clicks outside or presses Escape (to close the dropdown) */
  onClose?: () => void
  children: React.ReactNode
}

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Controlled InlineSelect component.
 *
 * Composable wrapper for InlineSelectTrigger, InlineSelectMenu, and InlineSelectOption.
 * Renders inline (span) to blend with surrounding text.
 * Does NOT manage open/close state - parent controls via isOpen prop.
 * All visual state determined by props. Spreads ...props for HOC injection.
 *
 * Calls onClose when:
 * - User clicks outside the select
 * - User presses Escape key
 *
 * @example
 * <span>
 *   with <InlineSelect isOpen={isOpen} onClose={handleClose}>
 *     <InlineSelectTrigger isOpen={isOpen} onToggle={handleToggle}>
 *       {selectedLabel}
 *     </InlineSelectTrigger>
 *     <InlineSelectMenu isOpen={isOpen}>
 *       {options.map(opt => (
 *         <InlineSelectOption
 *           key={opt.id}
 *           value={opt.id}
 *           selected={selectedValue === opt.id}
 *           onSelect={handleSelect}
 *         >
 *           {opt.label}
 *         </InlineSelectOption>
 *       ))}
 *     </InlineSelectMenu>
 *   </InlineSelect> modifiers
 * </span>
 */
const InlineSelect = React.memo(
  ({
    isOpen = false,
    disabled = false,
    onClose,
    children,
    style,
    className,
    ...props
  }: InlineSelectProps) => {
    const [css] = useStyletron()
    const containerRef = useRef<HTMLSpanElement>(null)

    // Handle click outside
    const handleClickOutside = useCallback(
      (event: MouseEvent) => {
        if (
          isOpen &&
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          onClose?.()
        }
      },
      [isOpen, onClose]
    )

    // Handle escape key
    const handleKeyDown = useCallback(
      (event: KeyboardEvent) => {
        if (isOpen && event.key === 'Escape') {
          onClose?.()
        }
      },
      [isOpen, onClose]
    )

    // NOTE: useEffect is allowed here because it's for event listener cleanup,
    // not for state initialization or side effects. The component remains controlled.
    useEffect(() => {
      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('keydown', handleKeyDown)
        return () => {
          document.removeEventListener('mousedown', handleClickOutside)
          document.removeEventListener('keydown', handleKeyDown)
        }
      }
      return undefined
    }, [isOpen, handleClickOutside, handleKeyDown])

    const containerClassName = css(getContainerStyles())

    return (
      <span
        ref={containerRef}
        data-open={isOpen}
        data-disabled={disabled}
        className={`${containerClassName} ${className || ''}`}
        style={style}
        {...props}
      >
        {children}
      </span>
    )
  }
)

// ═══════════════════════════════════════════════════════════════════════════
// Styles
// ═══════════════════════════════════════════════════════════════════════════

const getContainerStyles = () => ({
  position: 'relative' as const,
  display: 'inline-block',
})

export default InlineSelect
